import Icon from "@components/ui/icon";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { useGetCurrentUserQuery, useGetUsersQuery } from "@/shared/services";
import type { IMediaFolderUser, MediaFolderPermEnum } from "@/shared/types";
import { MediaLibMembersFolderDialogUserType } from "./media-lib-folder-members-dialog-user-type";

interface IProps {
  selectedUsers: IMediaFolderUser[];
  setSelectedUsers: Dispatch<SetStateAction<IMediaFolderUser[]>>;
}

export const MediaLibFolderMembersDialog = ({
  selectedUsers,
  setSelectedUsers,
}: IProps) => {
  return (
    <ol className="scrollbar tw-m-0 tw-mt-4 tw-max-h-48 tw-list-none tw-overflow-y-auto">
      {selectedUsers.map((user) => (
        <SelectedUser
          key={user.id}
          user={user}
          setUserType={(type) =>
            setSelectedUsers((prev) => {
              const idx = prev.findIndex((el) => el.id === user.id);
              if (idx >= 0) {
                const tmp = [...prev];
                tmp[idx] = {
                  ...tmp[idx],
                  type,
                };
                return tmp;
              }
              return prev;
            })
          }
          onDelete={() => {
            setSelectedUsers((prev) => {
              const idx = prev.findIndex((el) => el.id === user.id);
              if (idx >= 0) {
                const tmp = [...prev];
                tmp.splice(idx, 1);
                return tmp;
              }
              return prev;
            });
          }}
        />
      ))}
    </ol>
  );
};

interface ISelectedUserProps {
  user: IMediaFolderUser;
  onDelete: () => void;
  setUserType: (type: MediaFolderPermEnum) => void;
}

const SelectedUser = ({ user, onDelete, setUserType }: ISelectedUserProps) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allUsers = [] } = useGetUsersQuery();
  const [isPicking, setIsPicking] = useState(false);

  const isCurrentUser = currentUser?.oktaId === user.id;

  const userData = useMemo(() => {
    const data = allUsers.find((el) => el.oktaId === user.id);
    return data;
  }, [allUsers, user]);

  const initials = userData?.firstName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");

  const bgColor = userData?.profile?.color ?? "#D0CDD8";
  const textColor = userData?.profile?.theme === "dark" ? "white" : "#413565";
  const picture = userData?.profile?.picture;

  return (
    <li
      className={cn(
        "tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded tw-p-2 hover:tw-bg-blue-50",
        isPicking && "tw-bg-blue-50",
        // isCurrentUser && "tw-pointer-events-none",
      )}
      onMouseLeave={isPicking ? () => setIsPicking(false) : undefined}
    >
      <div
        className="tw-grid tw-size-8 tw-place-items-center tw-overflow-hidden tw-rounded-full"
        style={{
          background: bgColor,
        }}
      >
        {picture ? (
          <img
            src={picture}
            alt={userData?.firstName}
            className="tw-block tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <span
            className="tw-font-sans tw-text-xs/tight tw-font-semibold tw-uppercase"
            style={{
              color: textColor,
            }}
          >
            {initials}
          </span>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-justify-center">
        <div className="tw-text-[11px] tw-leading-normal tw-text-blue-800">
          {userData?.firstName}
          {isCurrentUser ? " (you)" : ""}
        </div>
        <div className="tw-text-[9px] tw-leading-normal tw-text-blue-500">
          {userData?.email}
        </div>
      </div>
      <div
        className={cn("tw-ml-auto", isCurrentUser && "tw-pointer-events-none")}
      >
        <MediaLibMembersFolderDialogUserType
          isPicking={isPicking}
          setIsPicking={setIsPicking}
          user={user}
          setUserType={setUserType}
        />
      </div>
      <Tooltip open={isCurrentUser ? undefined : false}>
        <Tooltip.Trigger>
          <button
            type="button"
            disabled={isCurrentUser}
            className={cn(
              "tw-grid tw-size-6 tw-place-items-center tw-rounded tw-text-blue-500 tw-opacity-0 tw-outline-none tw-transition-all enabled:hover:tw-bg-red-base/15 enabled:hover:tw-text-red-600 disabled:tw-cursor-not-allowed group-hover:tw-opacity-100",
            )}
            onClick={isCurrentUser ? undefined : onDelete}
          >
            <Icon icon="ri-delete-bin-line" className="tw-text-[12px]/none" />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content className="tw-z-[10003]">
          <Tooltip.Text>You cannot remove yourself</Tooltip.Text>
        </Tooltip.Content>
      </Tooltip>
    </li>
  );
};
